import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Mail, 
  HelpCircle, 
  LogOut, 
  XCircle,
  CheckCircle2,
  Calendar,
  AlertCircle
} from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { authService } from '../services/auth';
import { subscriptionService } from '../services/subscription';
import { toast } from '../hooks/use-toast';
import { CONTACT_EMAIL } from '../config';

const Settings = () => {
  const navigate = useNavigate();
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [canceling, setCanceling] = useState(false);
  /* Por qué cancela. Viaja con la cancelación y no la condiciona. */
  const [motivoCancelacion, setMotivoCancelacion] = useState('');
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [cancellationDate, setCancellationDate] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);

  // Cargar estado de suscripción al montar el componente
  useEffect(() => {
    const loadSubscriptionStatus = async () => {
      try {
        setLoadingStatus(true);
        const statusResponse = await subscriptionService.getStatus();
        
        if (statusResponse?.success && statusResponse?.data) {
          setSubscriptionStatus(statusResponse.data);
          
          // Si ya está cancelada, formatear la fecha de cancelación
          if (statusResponse.data.cancel_at_period_end && statusResponse.data.current_period_end) {
            try {
              const endDateObject = new Date(statusResponse.data.current_period_end);
              // Verificar que la fecha sea válida
              if (!isNaN(endDateObject.getTime())) {
                const dateStr = endDateObject.toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                });
                const timeStr = endDateObject.toLocaleTimeString('es-ES', {
                  hour: '2-digit',
                  minute: '2-digit',
                });
                setCancellationDate(`${dateStr}, a las ${timeStr}`);
              } else {
                console.warn('Fecha inválida recibida del backend:', statusResponse.data.current_period_end);
              }
            } catch (dateError) {
              console.warn('Error formateando fecha de cancelación:', dateError, 'Fecha recibida:', statusResponse.data.current_period_end);
            }
          }
        }
      } catch (error) {
        console.error('Error cargando estado de suscripción:', error);
        // Si no tiene suscripción, no hacer nada
      } finally {
        setLoadingStatus(false);
      }
    };

    loadSubscriptionStatus();
  }, []);

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const handleCancelSubscription = async () => {
    try {
      setCanceling(true);

      // Llamar al servicio real de cancelación
      const result = await subscriptionService.cancelSubscription(motivoCancelacion);
      // result esperado: { success, message, data: { subscription_id, status, cancel_at_period_end, current_period_end } }

      if (result?.success) {
        // Formatear fecha de finalización si viene
        let endDateFormatted = null;
        let endDateObject = null;
        if (result?.data?.current_period_end) {
          try {
            endDateObject = new Date(result.data.current_period_end);
            // Formato: "15 de enero de 2025, a las 23:59"
            const dateStr = endDateObject.toLocaleDateString('es-ES', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            });
            const timeStr = endDateObject.toLocaleTimeString('es-ES', {
              hour: '2-digit',
              minute: '2-digit',
            });
            endDateFormatted = `${dateStr}, a las ${timeStr}`;
          } catch (dateError) {
            console.warn('Error formateando fecha:', dateError);
          }
        }

        // Guardar la fecha para mostrarla en el diálogo de éxito
        setCancellationDate(endDateFormatted);
        
        // Actualizar el estado de la suscripción
        setSubscriptionStatus({
          ...subscriptionStatus,
          cancel_at_period_end: result.data.cancel_at_period_end,
          current_period_end: result.data.current_period_end,
        });
        
        // Cerrar el diálogo de confirmación
        setShowCancelDialog(false);
        setMotivoCancelacion('');

        // Mostrar el diálogo de éxito con la fecha
        setShowSuccessDialog(true);
      } else {
        toast({
          title: "No se pudo cancelar la suscripción",
          description: result?.error || result?.message || "Inténtalo de nuevo más tarde.",
          variant: "destructive",
        });
        setShowCancelDialog(false);
      }
    } catch (error) {
      console.error("Error cancelando suscripción:", error);

      // Intentar obtener mensaje del backend si existe
      const backendMessage =
        error?.data?.message ||
        error?.data?.error ||
        error?.message ||
        "Ocurrió un problema al procesar tu solicitud.";

      toast({
        title: "Error al cancelar suscripción",
        description: backendMessage,
        variant: "destructive",
      });
      setShowCancelDialog(false);
    } finally {
      setCanceling(false);
    }
  };

  // Determinar si la suscripción ya está cancelada
  const isSubscriptionCancelled = subscriptionStatus?.cancel_at_period_end === true;
  
  // Formatear fecha de cancelación para mostrar en el banner
  const formatCancellationDate = (dateString) => {
    if (!dateString) return null;
    try {
      const endDateObject = new Date(dateString);
      // Verificar que la fecha sea válida
      if (isNaN(endDateObject.getTime())) {
        console.warn('Fecha inválida:', dateString);
        return null;
      }
      const dateStr = endDateObject.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      const timeStr = endDateObject.toLocaleTimeString('es-ES', {
        hour: '2-digit',
        minute: '2-digit',
      });
      return `${dateStr}, a las ${timeStr}`;
    } catch (error) {
      console.warn('Error formateando fecha:', error, 'Fecha recibida:', dateString);
      return null;
    }
  };

  const settingsItems = [
    {
      id: 'contact',
      title: 'Contacto',
      description: 'Información de contacto',
      icon: Mail,
      action: () => {
        window.open(`mailto:${CONTACT_EMAIL}`, '_blank');
      },
    },
    {
      id: 'support',
      title: 'Soporte técnico',
      description: 'Obtén ayuda con tu cuenta',
      icon: HelpCircle,
      action: () => {
        window.open(`mailto:${CONTACT_EMAIL}?subject=Soporte Técnico`, '_blank');
      },
    },
    {
      id: 'cancel',
      title: 'Cancelar mis beneficios',
      description: isSubscriptionCancelled 
        ? (subscriptionStatus?.current_period_end 
            ? `Cancelación programada para el ${formatCancellationDate(subscriptionStatus.current_period_end)}`
            : 'Ya has cancelado tu suscripción')
        : 'Cancelar mi suscripción',
      icon: XCircle,
      action: () => setShowCancelDialog(true),
      variant: 'destructive',
      disabled: isSubscriptionCancelled,
    },
    {
      id: 'logout',
      title: 'Cerrar sesión',
      description: 'Salir de tu cuenta',
      icon: LogOut,
      action: handleLogout,
      variant: 'outline',
    },
  ];

  return (
    <>
      <div className="px-6 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          <div>
            <h2 className="font-display text-vd-h2 text-vd-tinta mb-2">Configuración</h2>
            <p className="text-vd-pizarra">Gestiona tu cuenta y preferencias</p>
          </div>

          {/* Alert: Suscripción Cancelada */}
          {!loadingStatus && isSubscriptionCancelled && (
            <div className="bg-yellow-50 border border-yellow-300/60 rounded-2xl p-5">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <h3 className="text-yellow-700 font-semibold text-lg">
                    Tu suscripción ha sido cancelada
                  </h3>
                  <div className="space-y-2 text-yellow-700/90">
                    {subscriptionStatus?.current_period_end ? (
                      <>
                        <p>
                          Tu suscripción se cancelará automáticamente el{' '}
                          <span className="font-bold text-yellow-800 text-base">
                            {formatCancellationDate(subscriptionStatus.current_period_end)}
                          </span>.
                        </p>
                        <p className="text-sm">
                          Hasta esa fecha, tu suscripción seguirá activa y podrás disfrutar de todos tus beneficios.
                        </p>
                      </>
                    ) : (
                      <p className="text-sm">
                        Tu suscripción será cancelada al final del período actual. Continuarás teniendo acceso hasta entonces.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Settings List */}
          <div className="space-y-3">
            {settingsItems.map((item) => {
              const Icon = item.icon;
              const isDisabled = item.disabled === true;
              
              return (
                <button
                  key={item.id}
                  onClick={isDisabled ? undefined : item.action}
                  disabled={isDisabled}
                  className={`w-full bg-white border border-vd-linea rounded-card shadow-warm-sm p-6 transition-all text-left ${
                    isDisabled
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:border-vd-naranja/40 cursor-pointer hover:scale-[1.02] active:scale-[0.98]'
                  } ${
                    item.variant === 'destructive' && !isDisabled ? 'hover:border-red-400/40' : ''
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                      item.variant === 'destructive'
                        ? isDisabled
                          ? 'bg-vd-niebla border border-vd-linea'
                          : 'bg-red-50 border border-red-200'
                        : item.variant === 'outline'
                        ? 'bg-vd-niebla border border-vd-linea'
                        : 'bg-vd-naranja/10 border border-vd-naranja/25'
                    }`}>
                      <Icon className={`w-6 h-6 ${
                        item.variant === 'destructive'
                          ? isDisabled
                            ? 'text-vd-pizarra/60'
                            : 'text-red-500'
                          : 'text-vd-grafito'
                      }`} />
                    </div>
                    
                    <div className="flex-1">
                      <h3 className={`text-base font-semibold mb-1 ${
                        item.variant === 'destructive'
                          ? isDisabled
                            ? 'text-vd-pizarra/60'
                            : 'text-red-500'
                          : 'text-vd-tinta'
                      }`}>
                        {item.title}
                      </h3>
                      <p className={`text-sm ${
                        isDisabled ? 'text-vd-pizarra/60' : 'text-vd-pizarra'
                      } ${item.id === 'cancel' && isSubscriptionCancelled && subscriptionStatus?.current_period_end ? 'leading-relaxed' : ''}`}>
                        {item.description}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Cancel Subscription Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="bg-white border border-vd-linea text-vd-tinta max-w-md rounded-card">
          <DialogHeader>
            <DialogTitle className="font-display text-vd-h2 text-vd-tinta">Cancelar suscripción</DialogTitle>
            <DialogDescription className="text-vd-pizarra">
              ¿Estás seguro de que deseas cancelar tu suscripción? Perderás acceso a todos los beneficios
              una vez que finalice tu período actual.
            </DialogDescription>
          </DialogHeader>

          {/*
            La pregunta, antes de confirmar.

            Iván (13-ago-2026): «que justo antes de terminar de cancelar les
            pregunte por qué están cancelando, con una caja de texto».

            Sin asterisco y sin bloquear el botón: quien no quiera explicarse
            cancela igual. Pedir una explicación como peaje no retiene a nadie
            —solo hace que la baja se recuerde como un mal trago— y llena la
            base de respuestas escritas de mala gana, que valen menos que nada
            porque además parecen datos.
          */}
          <div className="mt-2">
            <label htmlFor="motivo-cancelacion" className="block text-sm font-medium text-vd-grafito mb-2">
              ¿Por qué estás cancelando?
            </label>
            <textarea
              id="motivo-cancelacion"
              value={motivoCancelacion}
              onChange={(e) => setMotivoCancelacion(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="Cuéntanos qué pasó. Nos ayuda a mejorar."
              disabled={canceling}
              className="w-full border border-vd-linea rounded-input px-3 py-2 text-vd-tinta text-sm resize-none focus:outline-none focus:border-vd-grafito"
            />
            <p className="text-xs text-vd-pizarra mt-1.5">Opcional — puedes cancelar sin responder.</p>
          </div>

          <DialogFooter className="flex gap-3 mt-6">
            <Button
              variant="outline"
              onClick={() => setShowCancelDialog(false)}
              className="flex-1 border-vd-linea text-vd-grafito bg-transparent hover:bg-vd-niebla rounded-full"
              disabled={canceling}
            >
              No, mantener suscripción
            </Button>
            <Button
              onClick={handleCancelSubscription}
              disabled={canceling}
              className="flex-1 bg-red-500 text-white hover:bg-red-600 rounded-full"
            >
              {canceling ? 'Cancelando...' : 'Sí, cancelar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Success Alert Dialog - Cancelación Exitosa */}
      <AlertDialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <AlertDialogContent className="bg-white border border-vd-linea text-vd-tinta max-w-md rounded-card">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-full bg-green-50 border border-green-200 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
              <AlertDialogTitle className="font-display text-vd-h2 text-vd-tinta">
                ¡Cancelación exitosa!
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-vd-pizarra space-y-3 pt-2">
              <p className="text-base">
                Tu suscripción ha sido cancelada exitosamente. Continuarás teniendo acceso a todos los beneficios hasta el final de tu período actual.
              </p>

              {cancellationDate && (
                <div className="bg-vd-niebla border border-vd-linea rounded-input p-4 mt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="w-5 h-5 text-blue-500" />
                    <span className="text-vd-grafito font-semibold">Tu suscripción se cancelará el:</span>
                  </div>
                  <p className="text-blue-600 text-xl font-bold mb-3">
                    {cancellationDate}
                  </p>
                  <p className="text-vd-pizarra text-sm">
                    Hasta esa fecha y hora, tu suscripción seguirá activa y podrás seguir disfrutando de todos tus beneficios.
                  </p>
                </div>
              )}

              {!cancellationDate && (
                <p className="text-sm text-yellow-600 mt-2">
                  ⚠️ La fecha de cancelación no está disponible, pero tu suscripción será cancelada al final del período actual.
                </p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-6">
            <Button
              onClick={() => {
                setShowSuccessDialog(false);
                // Recargar el estado de la suscripción después de cerrar el diálogo
                const reloadStatus = async () => {
                  try {
                    const statusResponse = await subscriptionService.getStatus();
                    if (statusResponse?.success && statusResponse?.data) {
                      setSubscriptionStatus(statusResponse.data);
                      if (statusResponse.data.cancel_at_period_end && statusResponse.data.current_period_end) {
                        const formattedDate = formatCancellationDate(statusResponse.data.current_period_end);
                        setCancellationDate(formattedDate);
                      }
                    }
                  } catch (error) {
                    console.error('Error recargando estado:', error);
                  }
                };
                reloadStatus();
              }}
              className="w-full bg-white text-vd-naranja border border-vd-naranja hover:bg-vd-niebla rounded-full"
            >
              Entendido
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default Settings;

